import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { ArrowLeft, Check, X, Edit3, MapPin, FileSearch, AlertTriangle, ShieldCheck } from 'lucide-react';
import { api, extractApiError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { formatDate } from '@/lib/utils';
import { PdfViewer } from '@/components/documents/PdfViewer';
import { ESIG_REASON_CODES, type EsigAction } from '@praxara/shared/constants';

type Decision = 'approve' | 'reject' | 'correct';

interface CitationCheck {
  field: string;
  page: number;
  hasQuote: boolean;
  ok: boolean;
  validations: Array<{ kind: string; ok: boolean; note?: string }>;
}
interface CitationValidationResponse {
  total: number;
  valid: number;
  invalid: number;
  pageCount: number | null;
  hasExtractedText: boolean;
  checks: CitationCheck[];
}

export function ExtractionReviewPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [jsonText, setJsonText] = useState<string>('');
  const [jsonEditing, setJsonEditing] = useState(false);
  const [esignOpen, setEsignOpen] = useState(false);
  const [decision, setDecision] = useState<Decision>('approve');
  const [esignPassword, setEsignPassword] = useState('');
  const [esignReasonCode, setEsignReasonCode] = useState('');
  const [esignReason, setEsignReason] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');
  // Reject + reclassify state
  const [reclassifyOpen, setReclassifyOpen] = useState(false);
  const [newDocType, setNewDocType] = useState<string>('');
  const [reclassifyReason, setReclassifyReason] = useState('');
  // Citations: when the reviewer clicks one, jump the PDF viewer to that page.
  const [jumpToPage, setJumpToPage] = useState<number | undefined>();
  const [jumpKey, setJumpKey] = useState(0);
  const [activeCitationField, setActiveCitationField] = useState<string | null>(null);
  function clickCitation(field: string, page: number) {
    setActiveCitationField(field);
    setJumpToPage(page);
    setJumpKey((k) => k + 1);
  }

  const extraction = useQuery({
    queryKey: ['extraction', id],
    queryFn: async () => {
      const res = await api.get(`/api/extractions/${id}`);
      setJsonText(JSON.stringify(res.data.structuredData, null, 2));
      return res.data;
    },
  });

  const citationValidation = useQuery({
    queryKey: ['extraction', id, 'citation-validation'],
    queryFn: async () =>
      (await api.get<CitationValidationResponse>(`/api/extractions/${id}/citation-validation`)).data,
    enabled: !!id,
  });

  const reclassify = useMutation({
    mutationFn: async () => {
      if (!newDocType) throw new Error('Pick a new document type first.');
      // 1. Reject the extraction with e-signature
      await api.patch(`/api/extractions/${id}/review`, {
        decision: 'reject',
        notes: `Rejected for reclassification → ${newDocType}. ${reclassifyReason}`,
        esignature: {
          password: esignPassword,
          reason: `[REJECTED_FOR_RECLASSIFY] Document was wrong type — reclassifying to ${newDocType}`,
          reasonCode: 'REJECTED_FOR_RECLASSIFY',
        },
      });
      // 2. Reclassify the document
      const docId = extraction.data?.documentId;
      if (!docId) throw new Error('Document id missing on extraction.');
      return api.patch(`/api/documents/${docId}/reclassify`, {
        documentType: newDocType,
        reason: reclassifyReason || `Reviewer reclassified after rejecting AI extraction (was wrong document type).`,
      });
    },
    onSuccess: () => {
      toast.success('Reclassified', `Document → ${newDocType}. Run an applicable workflow next.`);
      setReclassifyOpen(false);
      const docId = extraction.data?.documentId;
      if (docId) navigate(`/documents/${docId}`);
    },
    onError: (err) => {
      toast.error('Reclassify failed', extractApiError(err).message);
    },
  });

  const review = useMutation({
    mutationFn: async () => {
      let corrections: Record<string, unknown> | undefined;
      if (decision === 'correct') {
        try {
          corrections = JSON.parse(jsonText);
        } catch {
          throw new Error('Corrected JSON is not valid. Fix syntax and try again.');
        }
      }
      return api.patch(`/api/extractions/${id}/review`, {
        decision,
        notes: reviewNotes || undefined,
        corrections,
        esignature: {
          password: esignPassword,
          reason: esignReasonCode ? `[${esignReasonCode}] ${esignReason}`.trim() : esignReason,
          reasonCode: esignReasonCode,
        },
      });
    },
    onSuccess: () => {
      toast.success('Review submitted', `Decision: ${decision}`);
      setEsignOpen(false);
      setEsignPassword('');
      setEsignReason('');
      if (extraction.data?.workflowRunId) {
        navigate(`/workflows/runs/${extraction.data.workflowRunId}`);
      } else {
        navigate(`/documents/${extraction.data?.documentId}`);
      }
    },
    onError: (err) => {
      toast.error('Review failed', extractApiError(err).message);
    },
  });

  if (extraction.isLoading) return <div>Loading...</div>;
  if (!extraction.data) return <div>Not found</div>;

  const e = extraction.data;
  const meta = e.structuredData?._extraction ?? {};

  function openSign(d: Decision) {
    setDecision(d);
    setEsignOpen(true);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <Button asChild variant="ghost" size="sm" className="-ml-2 mb-2">
            <Link to={e.workflowRunId ? `/workflows/runs/${e.workflowRunId}` : '/documents'}>
              <ArrowLeft className="mr-2 h-4 w-4" /> Back
            </Link>
          </Button>
          <h1 className="text-2xl font-bold">Review extraction</h1>
          <p className="text-sm text-muted-foreground">
            {e.document.fileName} · {formatDate(e.createdAt)}
          </p>
        </div>
        <Badge
          variant={
            e.reviewStatus === 'APPROVED'
              ? 'success'
              : e.reviewStatus === 'REJECTED'
                ? 'destructive'
                : 'warning'
          }
        >
          {e.reviewStatus}
        </Badge>
      </div>

      {/* Split view: PDF left, JSON right */}
      <div className="grid gap-4 lg:grid-cols-2">
        {/* Source document */}
        <div className="h-[calc(100vh-14rem)]">
          {e.document.mimeType === 'application/pdf' ? (
            <PdfViewer
              documentId={e.documentId}
              fileName={e.document.fileName}
              className="h-full"
              highlightPage={jumpToPage}
              jumpKey={jumpKey}
            />
          ) : (
            <Card className="flex h-full flex-col">
              <CardHeader className="flex-shrink-0">
                <CardTitle>Source document</CardTitle>
              </CardHeader>
              <CardContent className="flex-1 overflow-auto p-4">
                <div className="text-sm text-muted-foreground">
                  Inline preview is available for PDFs. Download this document to view the original.
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Extracted JSON */}
        <Card className="flex h-[calc(100vh-14rem)] flex-col">
          <CardHeader className="flex flex-shrink-0 flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle>Extracted data</CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setJsonEditing((v) => !v)}
            >
              <Edit3 className="mr-1.5 h-3.5 w-3.5" />
              {jsonEditing ? 'Lock' : 'Edit'}
            </Button>
          </CardHeader>
          <CardContent className="flex-1 overflow-hidden p-0">
            <textarea
              readOnly={!jsonEditing}
              value={jsonText}
              onChange={(e) => setJsonText(e.target.value)}
              className="scrollbar-thin h-full w-full resize-none border-0 bg-muted/30 p-4 font-mono text-xs focus:outline-none"
              spellCheck={false}
            />
          </CardContent>
        </Card>
      </div>

      {/* Meta + flags */}
      <Card>
        <CardContent className="flex flex-wrap items-center gap-6 p-4">
          <div>
            <div className="text-xs text-muted-foreground">Confidence</div>
            <div className="font-medium">{meta.confidence ?? '—'}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Flags for review</div>
            <div className="flex flex-wrap gap-1">
              {(meta.flagsForReview ?? []).length === 0 ? (
                <span className="text-muted-foreground">None</span>
              ) : (
                meta.flagsForReview.map((f: string) => (
                  <Badge key={f} variant="warning">
                    {f}
                  </Badge>
                ))
              )}
            </div>
          </div>
          <div className="flex-1" />
          <div>
            <div className="text-xs text-muted-foreground">Validation</div>
            <Badge variant={e.validationStatus === 'PASSED' ? 'success' : 'destructive'}>
              {e.validationStatus}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Citations: click a row to jump the PDF viewer to that page */}
      {Array.isArray(meta.citations) && meta.citations.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <FileSearch className="h-4 w-4 text-primary" /> Source-page citations
              </CardTitle>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {meta.citations.length} fields cite a source page. Click a row to jump the PDF viewer.
              </p>
            </div>
            {citationValidation.data && (
              <Badge
                variant={citationValidation.data.invalid === 0 ? 'success' : 'destructive'}
                className="gap-1"
              >
                {citationValidation.data.invalid === 0 ? (
                  <ShieldCheck className="h-3 w-3" />
                ) : (
                  <AlertTriangle className="h-3 w-3" />
                )}
                {citationValidation.data.valid}/{citationValidation.data.total} valid
              </Badge>
            )}
          </CardHeader>
          <CardContent className="p-0">
            <div className="grid max-h-72 grid-cols-1 gap-px overflow-y-auto bg-border md:grid-cols-2 lg:grid-cols-3">
              {(meta.citations as Array<{ field: string; page: number }>).map((c, i) => {
                const isActive = activeCitationField === c.field;
                const check = citationValidation.data?.checks.find(
                  (k) => k.field === c.field && k.page === c.page,
                );
                const checkOk = check?.ok ?? true;
                const failNote = check?.validations.find((v) => !v.ok)?.note;
                return (
                  <button
                    key={`${c.field}-${i}`}
                    onClick={() => clickCitation(c.field, c.page)}
                    className={`flex items-start gap-2 bg-card px-3 py-2 text-left text-xs transition-colors hover:bg-accent/40 ${
                      isActive ? 'ring-2 ring-inset ring-primary' : ''
                    } ${!checkOk ? 'bg-destructive/5' : ''}`}
                    title={failNote ?? `Jump to page ${c.page}`}
                  >
                    {check && !checkOk ? (
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-destructive" />
                    ) : check && checkOk ? (
                      <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
                    ) : (
                      <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-mono text-[11px] text-foreground">{c.field}</div>
                      <div className="text-[10px] text-muted-foreground">
                        page {c.page}
                        {check && !checkOk && failNote && ` -- ${failNote}`}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            {citationValidation.data && citationValidation.data.invalid > 0 && (
              <div className="border-t bg-destructive/5 px-3 py-2 text-[11px] text-destructive">
                {citationValidation.data.invalid} citation{citationValidation.data.invalid === 1 ? '' : 's'} failed
                validation. Reject and reclassify, or edit the JSON to fix the page reference before approving.
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Review notes + actions */}
      {e.reviewStatus === 'PENDING' && (
        <Card>
          <CardHeader>
            <CardTitle>Your review</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="notes">Notes (optional)</Label>
              <Textarea
                id="notes"
                value={reviewNotes}
                onChange={(ev) => setReviewNotes(ev.target.value)}
                placeholder="Observations, reasoning, any adjustments made."
                rows={3}
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => openSign('approve')}>
                <Check className="mr-1.5 h-4 w-4" /> Approve
              </Button>
              <Button variant="outline" onClick={() => openSign('correct')} disabled={!jsonEditing}>
                <Edit3 className="mr-1.5 h-4 w-4" /> Submit corrections
              </Button>
              <Button variant="destructive" onClick={() => openSign('reject')}>
                <X className="mr-1.5 h-4 w-4" /> Reject
              </Button>
              <Button variant="outline" onClick={() => setReclassifyOpen(true)}>
                <FileSearch className="mr-1.5 h-4 w-4" /> Reject + reclassify
              </Button>
            </div>
            {!jsonEditing && (
              <p className="text-xs text-muted-foreground">
                To submit corrections, click &quot;Edit&quot; above and modify the JSON first.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* E-signature modal */}
      <Dialog open={esignOpen} onOpenChange={setEsignOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Electronic signature required</DialogTitle>
            <DialogDescription>
              Per 21 CFR Part 11, confirm your identity and state a reason for this action.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="esign-password">Your password</Label>
              <Input
                id="esign-password"
                type="password"
                autoFocus
                value={esignPassword}
                onChange={(ev) => setEsignPassword(ev.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="esign-reason-code">Reason code <span className="text-destructive">*</span></Label>
              <select
                id="esign-reason-code"
                value={esignReasonCode}
                onChange={(ev) => setEsignReasonCode(ev.target.value)}
                required
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value="">Select a reason code...</option>
                {ESIG_REASON_CODES[decision as EsigAction].map((r) => (
                  <option key={r.code} value={r.code}>{r.label}</option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="esign-reason">Additional notes (optional)</Label>
              <Input
                id="esign-reason"
                value={esignReason}
                onChange={(ev) => setEsignReason(ev.target.value)}
                placeholder={
                  decision === 'approve'
                    ? 'e.g. Verified against CSR Section 10, Tables 14.3.1-14.3.4'
                    : decision === 'reject'
                      ? 'e.g. Primary endpoint was Week 52, not Week 24 as extracted'
                      : 'e.g. Corrected AE counts per Table 14.3.1 (source verified)'
                }
              />
            </div>
            <div className="rounded-md border bg-muted/30 p-3 text-xs">
              <div>Action: <span className="font-semibold capitalize">{decision}</span></div>
              <div>Signed as: <span className="font-semibold">{e.document.fileName}</span></div>
              <div>At: <span className="font-semibold">{new Date().toLocaleString()}</span></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEsignOpen(false)}>
              Cancel
            </Button>
            <Button onClick={() => review.mutate()} disabled={review.isPending}>
              {review.isPending ? 'Signing...' : 'Sign & submit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject + reclassify modal */}
      <Dialog open={reclassifyOpen} onOpenChange={setReclassifyOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject + reclassify</DialogTitle>
            <DialogDescription>
              The AI extracted against the wrong document type. Reject this extraction (with e-signature),
              re-tag the document, then route to the correct workflow.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="reclass-type">New document type</Label>
              <select
                id="reclass-type"
                value={newDocType}
                onChange={(ev) => setNewDocType(ev.target.value)}
                className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm"
              >
                <option value="">Pick a type…</option>
                <option value="CLINICAL_STUDY_REPORT">Clinical Study Report (CSR)</option>
                <option value="CLINICAL_OVERVIEW">Clinical Overview (CTD 2.5)</option>
                <option value="CLINICAL_SUMMARY">Clinical Summary (CTD 2.7)</option>
                <option value="REGULATORY_SUBMISSION">Regulatory Submission / EPAR / Assessment Report</option>
                <option value="INVESTIGATOR_BROCHURE">Investigator's Brochure</option>
                <option value="CLINICAL_STUDY_PROTOCOL">Clinical Study Protocol</option>
                <option value="ICSR">Individual Case Safety Report (ICSR)</option>
                <option value="PSUR">PSUR / PBRER</option>
                <option value="RISK_MANAGEMENT_PLAN">Risk Management Plan</option>
                <option value="PSMF">PSMF</option>
                <option value="SMPC">SmPC</option>
                <option value="SOP">SOP</option>
                <option value="OTHER">Other</option>
              </select>
            </div>
            <div>
              <Label htmlFor="reclass-reason">Reason</Label>
              <Textarea
                id="reclass-reason"
                value={reclassifyReason}
                onChange={(ev) => setReclassifyReason(ev.target.value)}
                placeholder="e.g. AI flagged not-a-csr; this is an EMA EPAR (CHMP Assessment Report). Re-routing to EPAR Overview Extraction."
                rows={3}
              />
            </div>
            <div>
              <Label htmlFor="reclass-pw">Your password (e-signature)</Label>
              <Input
                id="reclass-pw"
                type="password"
                value={esignPassword}
                onChange={(ev) => setEsignPassword(ev.target.value)}
              />
            </div>
            <div className="rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground">
              Two audit-log rows are written: a WORKFLOW_REJECTED with your e-signature, then a
              DOCUMENT_PROCESSED reclassification with your reason.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReclassifyOpen(false)}>Cancel</Button>
            <Button onClick={() => reclassify.mutate()} disabled={reclassify.isPending || !newDocType || !esignPassword}>
              {reclassify.isPending ? 'Submitting...' : 'Reject + reclassify'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
